import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import Button from "../elements/Button";
import MenuTag from "./MenuTag";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useClick,
  useInteractions,
} from "@floating-ui/react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function DropdownMenu({
  menuItems,
  buttonText,
  buttonColor,
  showCheveron,
  buttonType,
  direction,
}) {
  console.log("Menu Items", menuItems);

  const [isOpen, setIsOpen] = useState(false);

  // Floating UI hooks for proper positioning
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-end", // Adjust placement as needed
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  // Dismiss dropdown when clicking outside
  const dismiss = useDismiss(context);
  const click = useClick(context, { toggle: true });

  // Merge all interactions
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const groupedMenuItems = menuItems.reduce((groupedItems, item) => {
    if (item.show) {
      const key = item.group;
      if (!groupedItems[key]) {
        groupedItems[key] = [];
      }
      groupedItems[key].push(item);
    }
    return groupedItems;
  }, {});

  return Object.keys(groupedMenuItems).length > 0 ? (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          ref={refs.setReference}
          {...getReferenceProps()}
          className="inline-flex w-full justify-center rounded-full px-0 py-0"
        >
          {buttonType === "icon" ? (
            <Button iconButton>
              <Icon
                className="h-5 w-5 text-slate-500"
                icon="mdi:dots-vertical"
              />
            </Button>
          ) : (
            <Button color={buttonColor || "outline"} size="sm">
              {buttonText || "Menu"}
              <Icon className="h-5 w-5 ml-2" icon="mdi:chevron-down" />
            </Button>
          )}
        </Menu.Button>
      </div>

      <FloatingPortal>
        <Menu.Items
          ref={refs.setFloating}
          {...getFloatingProps()}
          style={floatingStyles} // Ensure correct positioning before animation starts
          hidden={!isOpen} // Prevents initial jump
          className="absolute z-50 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        >
          <Transition
            as={Fragment}
            show={isOpen}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="py-1.5">
              {Object.entries(groupedMenuItems).map(([group, items], index) => (
                <div
                  key={group}
                  className={`${
                    index !== 0 &&
                    Object.entries(groupedMenuItems).length > 1 &&
                    "border-t"
                  } border-gray-200`}
                >
                  {items.map(
                    (item, idx) =>
                      item.show === true && (
                        <Menu.Item key={idx}>
                          {({ active }) => (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                item.action(e);
                                setIsOpen(false);
                              }}
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700",
                                !item.disabled
                                  ? "cursor-pointer"
                                  : "cursor-not-allowed",
                                "group flex items-center px-4 py-3 text-sm w-full text-left"
                              )}
                            >
                              <Icon
                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                icon={item.icon}
                                aria-hidden="true"
                              />
                              {item.name}
                              {item.status && <MenuTag status={item.status} />}
                            </button>
                          )}
                        </Menu.Item>
                      )
                  )}
                </div>
              ))}
            </div>
          </Transition>
        </Menu.Items>
      </FloatingPortal>
    </Menu>
  ) : null;
}
